import { useCallback, useEffect, useState } from 'react'
import {
  loadAndOpenLandingCampaignWidgetWithCoupon,
  loadAndOpenLandingCampaignWidgetForRanking,
  loadLandingCampaignWidget,
  openLandingCampaignWidget,
  unloadLandingCampaignWidget,
  type LoadLandingCampaignWidgetOptions,
  type LandingPendingCouponPrize,
} from '@/lib/landingCampaignWidget'
import { campaignSalesClosedReason, isCampaignOpenForSales } from '@/lib/campaign-sales'
import type { LandingCampaign } from '@/types/landing'

function profileCheckoutOptions(
  donorId?: number
): LoadLandingCampaignWidgetOptions | undefined {
  return donorId && donorId > 0
    ? { profileCheckout: true, profileDonorId: donorId }
    : undefined
}

export function useLandingCampaignWidget(
  campaign: LandingCampaign | null,
  donorId?: number
) {
  const [isOpening, setIsOpening] = useState(false)
  const [widgetError, setWidgetError] = useState<string | null>(null)
  const campaignId = campaign?.id

  useEffect(() => {
    return () => {
      if (campaignId != null) {
        unloadLandingCampaignWidget(campaignId)
      }
    }
  }, [campaignId])

  const openPurchaseWidget = useCallback(() => {
    if (!campaign?.id || isOpening) {
      return
    }

    if (!isCampaignOpenForSales(campaign)) {
      setWidgetError(campaignSalesClosedReason(campaign))
      return
    }

    setWidgetError(null)
    setIsOpening(true)
    void loadLandingCampaignWidget(campaign, profileCheckoutOptions(donorId))
      .then((instanceId) => {
        openLandingCampaignWidget(instanceId, true)
      })
      .catch(() => {
        unloadLandingCampaignWidget(campaign.id)
        setWidgetError(
          'Não foi possível abrir a compra agora. Tente novamente em instantes.'
        )
      })
      .finally(() => {
        setIsOpening(false)
      })
  }, [campaign, donorId, isOpening])

  const openPurchaseWidgetWithCoupon = useCallback(
    (coupon: LandingPendingCouponPrize): Promise<void> => {
      if (!campaign?.id || isOpening) {
        return Promise.resolve()
      }

      if (!isCampaignOpenForSales(campaign)) {
        const reason = campaignSalesClosedReason(campaign)
        setWidgetError(reason)
        return Promise.reject(new Error(reason))
      }

      setWidgetError(null)
      setIsOpening(true)
      return loadAndOpenLandingCampaignWidgetWithCoupon(
        campaign,
        coupon,
        profileCheckoutOptions(donorId)
      )
        .then(() => undefined)
        .catch((error) => {
          unloadLandingCampaignWidget(campaign.id)
          setWidgetError(
            'Não foi possível abrir a compra com cupom. Tente novamente em instantes.'
          )
          throw error
        })
        .finally(() => {
          setIsOpening(false)
        })
    },
    [campaign, donorId, isOpening]
  )

  const openPurchaseWidgetForRanking = useCallback(
    (quantity: number, expectedAmountCents: number): Promise<void> => {
      if (!campaign?.id || isOpening) return Promise.reject(new Error('widget_busy'))
      if (!isCampaignOpenForSales(campaign)) {
        const reason = campaignSalesClosedReason(campaign)
        setWidgetError(reason)
        return Promise.reject(new Error(reason))
      }
      setWidgetError(null)
      setIsOpening(true)
      return loadAndOpenLandingCampaignWidgetForRanking(
        campaign,
        quantity,
        expectedAmountCents,
        profileCheckoutOptions(donorId)
      )
        .catch((error) => {
          if (
            error instanceof Error &&
            error.message === 'widget_ranking_payment_pending'
          ) {
            setWidgetError(
              'Já existe um Pix em andamento. Conclua ou cancele essa compra antes de escolher outra posição.'
            )
          } else {
            unloadLandingCampaignWidget(campaign.id)
            setWidgetError(
              'Não foi possível abrir este Pix. Tente novamente em instantes.'
            )
          }
          throw error
        })
        .finally(() => setIsOpening(false))
    },
    [campaign, donorId, isOpening]
  )

  return {
    openPurchaseWidget,
    openPurchaseWidgetWithCoupon,
    openPurchaseWidgetForRanking,
    isWidgetLoading: isOpening,
    widgetError,
  }
}
